import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted, nextTick, defineAsyncComponent, watch } from 'vue';
import { datadogRum } from '@datadog/browser-rum';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { sanitizeHTML } from '@white-label-helper/shared-helpers';
// Types
import type { SearchProducts } from '@white-label-types/parking-booking';
interface ExtrasMoreInfoModalProps {
  product: SearchProducts;
  tabs?: { id: string; translationKey: string; visible: boolean; active: boolean; content: string }[];
}
export default /*#__PURE__*/_defineComponent({
  __name: 'extras-more-info',
  props: {
    product: { default: () => ({}) },
    tabs: { default: () => [] }
  },
  emits: ['close', 'cta-click'],
  setup(__props: any, { expose, emit }) {

const props = __props as { product: SearchProducts, tabs: { id: string; translationKey: string; visible: boolean; active: boolean; content: string }[] };

// Packages
const IconArrow = defineAsyncComponent(() => import('@white-label-icon/icon-arrow'));
const IconClose = defineAsyncComponent(() => import('@white-label-icon/icon-close'));

;




const moreInfoModal = ref<HTMLElement | null>(null);
const isTabsScrollable = ref(false);
const isTabsScrollAtTheEnd = ref(false);
const showProductLogo = computed(() => getAppVariable('is_product_logo_visible') && Boolean(props.product.logo?.src));
const localTabs = ref(props.tabs.map(tab => ({ ...tab })));

function closeModal() {
  emit('close');
}

function goToTab(tabId: string) {
  datadogRum.addAction('more-info-modal', { value: tabId });
  scrollToElement(tabId);
}

function scrollToElement(id: string) {
  const el = document.getElementById(id);
  const scrollArea = document.querySelector('.more-info-modal__scroll-area');

  if (el instanceof HTMLElement && scrollArea instanceof HTMLElement) {
    // Calculate the element's position relative to the scroll area
    const elRect = el.getBoundingClientRect();
    const scrollRect = scrollArea.getBoundingClientRect();
    const offset = elRect.top - scrollRect.top + scrollArea.scrollTop;

    // If the element is above the visible area, scroll up
    if (offset < scrollArea.scrollTop) {
      scrollArea.scrollTo({ top: offset, behavior: 'smooth' });
    } else {
      // If the element is below the visible area, scroll down
      const elBottom = offset + el.offsetHeight;
      const elTop = offset;
      const visibleBottom = scrollArea.scrollTop + scrollArea.clientHeight;
      if (elBottom > visibleBottom) {
        scrollArea.scrollTo({ top: elTop, behavior: 'smooth' });
      }
    }
    setTimeout(() => {
      localTabs.value.forEach((tab) => {
        tab.active = tab.id === id;
      });
    }, 500);
  }
}

function checkIfTabsScrollable() {
  const el = document.querySelector('.more-info-modal__nav-tabs');
  if (el instanceof HTMLElement) {
    isTabsScrollable.value = el.scrollWidth > el.clientWidth;
  }
}

function navTabsScrollHandler(ev: Event) {
  if (ev.target instanceof HTMLElement) {
    isTabsScrollAtTheEnd.value = ev.target.scrollLeft === ev.target.scrollWidth - ev.target.clientWidth;
  }
}

function scrollLeftIconClickHandler() {
  const el = document.querySelector('.more-info-modal__nav-tabs');
  if (el instanceof HTMLElement) {
    el.scrollTo({ left: -100, behavior: 'smooth' });
  }
}

function scrollRightIconClickHandler() {
  const el = document.querySelector('.more-info-modal__nav-tabs');
  if (el instanceof HTMLElement) {
    el.scrollTo({ left: 100, behavior: 'smooth' });
  }
}

function initIntersectionObserver() {
  const onIntersection = (entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      const link = document.querySelector(`.more-info-modal__tab--${entry.target.id}`);
      const index = localTabs.value.findIndex((item) => item.id === entry.target.id);
      if (entry.isIntersecting && link instanceof HTMLElement) {
        localTabs.value[index].active = true;
      } else if (link instanceof HTMLElement) {
        localTabs.value[index].active = false;
      }
    });
  };

  const observer = new IntersectionObserver(onIntersection, {
    root: document.querySelector('.more-info-modal__scroll-area'),
    threshold: [0.2, 0.4],
  });

  const children = document.querySelector('.more-info-modal__scroll-area')?.children;
  if (children instanceof HTMLCollection) {
    Array.from(children).forEach((child) => {
      observer.observe(child);
    });
  }
}

function getSanitizedHTML(html: string): string {
  return sanitizeHTML(html);
}

expose({
  closeModal,
  goToTab,
  localTabs
});

onMounted(() => {
  initIntersectionObserver();
  nextTick(() => {
    checkIfTabsScrollable();
  });
});

return { __sfc: true,IconArrow, IconClose, props, emit, moreInfoModal, isTabsScrollable, isTabsScrollAtTheEnd, showProductLogo, localTabs, closeModal, goToTab, scrollToElement, checkIfTabsScrollable, navTabsScrollHandler, scrollLeftIconClickHandler, scrollRightIconClickHandler, initIntersectionObserver, getSanitizedHTML }
}

})